/**
 * FeedbackDialog Component
 *
 * Dialog for managers to provide feedback
 */

import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Feedback } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { okrApi } from '../../../services/okrApi';

export const FeedbackDialog: React.FC = () => {
    const __muiTheme = useTheme();
    const isMobile = useMediaQuery(__muiTheme.breakpoints.down("sm"));
    const { isDarkMode, data, dialogs } = useOKRDashboardContext();
    const { setSuccess, setError, refetchData } = data;
    const { dialogs: dialogState, formState, updateFormField, closeFeedbackDialog } = dialogs;

    const handleSubmit = async () => {
        if (!formState.feedbackText.trim() || !formState.selectedObjectiveForFeedback?.id) return;

        try {
            await okrApi.addNote(formState.selectedObjectiveForFeedback.id, formState.feedbackText);
            setSuccess('Feedback added successfully');
            closeFeedbackDialog();
            refetchData();
        } catch (err: any) {
            setError(err.message || 'Failed to add feedback');
        }
    };

    const objectiveOrEmployee = formState.selectedObjectiveForFeedback;
    const displayName = objectiveOrEmployee?.employee?.name || objectiveOrEmployee?.title || 'Team Member';

    return (
        <Dialog
            open={dialogState.feedbackDialog}
            onClose={closeFeedbackDialog}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Feedback sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Add Feedback
                        </Typography>
                        <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                            For: {displayName}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <TextField
                        autoFocus
                        label="Feedback"
                        fullWidth
                        multiline
                        rows={4}
                        value={formState.feedbackText}
                        onChange={(e) => updateFormField('feedbackText', e.target.value)}
                        placeholder="Provide constructive feedback, guidance, or recognition..."
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            },
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1 }}>
                <Button
                    onClick={closeFeedbackDialog}
                    sx={{
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        fontWeight: 600,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!formState.feedbackText.trim()}
                    sx={{
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                    }}
                >
                    Submit Feedback
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackDialog;
